import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, X, MapPin, ChevronDown, Wrench, DollarSign, 
  Smartphone, Tablet, Laptop, Gamepad2, Mail, MessageCircle,
  User, Clock, Shield, Award, CheckCircle, Zap, Send, Scale, Globe, Compass, Banknote
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import CoreTrustBadges from './CoreTrustBadges';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/store') || location.pathname.startsWith('/employee-portal');
  // Route-aware theme: only the homepage uses the dark/glassy nav. Every other public
  // route renders the light variant (white surface, dark text) to match the new design system.
  const isLightNav = true;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isAdminRoute) return null;

  const navItems = [
    {
      id: 'repairs',
      label: 'Repair',
      icon: Zap,
      gradient: 'from-cyan-500 to-blue-600',
      items: [
        { label: 'iPhone Repair', path: '/repairs/phones/iphone', icon: Smartphone, desc: 'Screen, Battery, Charging & More' },
        { label: 'Samsung Repair', path: '/repairs/phones/samsung', icon: Smartphone, desc: 'Galaxy S, Note, Z Series' },
        { label: 'iPad Repair', path: '/repairs/tablets/ipad', icon: Tablet, desc: 'All iPad Models' },
        { label: 'MacBook Repair', path: '/repairs/laptops/macbook', icon: Laptop, desc: 'Pro, Air, iMac' },
        { label: 'Gaming Console', path: '/repairs/playstation', icon: Gamepad2, desc: 'PS5, Xbox, Nintendo' },
        { label: 'All Repairs', path: '/repairs', icon: Wrench, desc: 'View All Services', highlight: true },
      ]
    },
    {
      id: 'sell',
      label: 'Sell Device',
      icon: DollarSign,
      gradient: 'from-green-500 to-emerald-600',
      items: [
        { label: 'Get Instant Quote', path: '/sell', icon: Zap, desc: 'Cash in 5 minutes' },
        { label: 'Cash Buyback', path: '/buyback', icon: Banknote, desc: 'Highest payouts in Chicago', highlight: true },
        { label: 'Trade-In Program', path: '/sell', icon: Award, desc: 'Upgrade & Save' },
        { label: 'Price Guide', path: '/all-sell', icon: DollarSign, desc: 'See What You Can Get' },
      ]
    },
  ];

  return (
    <>
    <header 
      className={`fixed left-0 right-0 z-[100] transition-all duration-300 ${
        isLightNav
          ? (isScrolled
              ? 'bg-white/95 backdrop-blur-xl shadow-[0_4px_24px_rgba(15,23,42,0.06)] border-b border-slate-200/70'
              : 'bg-white/80 backdrop-blur-md border-b border-slate-100')
          : (isScrolled 
              ? 'bg-[#07111F] shadow-[0_4px_24px_rgba(0,0,0,0.35)] border-b border-white/[0.08]' 
              : 'bg-[#07111F] border-b border-white/[0.08]')
      }`} 
      style={{ top: 0 }}
      data-testid="main-header"
    >
      {/* Core credentials trust band, featured above the booking/inquiry CTAs (tablet/desktop) */}
      <div className="hidden sm:block w-full bg-slate-900 border-b border-emerald-500/15" style={{ backgroundColor: '#0f172a' }} data-testid="header-trust-band">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-1.5 flex items-center justify-center gap-x-6 gap-y-1 flex-wrap text-[11px] lg:text-xs font-semibold text-white/90" style={{ color: 'rgba(255,255,255,0.9)' }}>
          <span className="inline-flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-emerald-400" /> Hundreds of Repairs Completed</span>
          <span className="text-white/15">•</span>
          <span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-emerald-400" /> Apple Independent Repair Provider</span>
          <span className="text-white/15">•</span>
          <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> 6-Month Repair Protection Plan</span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between px-3 sm:px-6 lg:px-8 py-2 sm:py-2.5">
          {/* Logo */}
          <button 
            onClick={() => handleNavClick('/')}
            className="flex items-center group flex-shrink-0"
            data-testid="logo"
          >
            <picture>
              <source srcSet="/images/logo.webp" type="image/webp" />
              <img 
                src="/images/logo.webp" 
                alt="2A Electronics Service" 
                className="h-6 sm:h-9 lg:h-11 object-contain transition-transform duration-200 group-hover:scale-105"
                loading="eager"
              />
            </picture>
            <span 
              className={`ml-1.5 sm:ml-2.5 font-bold text-xs sm:text-sm lg:text-base tracking-tight ${isLightNav ? 'text-slate-900' : 'text-white'}`}
              data-testid="header-brand-text"
            >
              2A Electronics
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav ref={dropdownRef} className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div key={item.id} className="relative">
                <button
                  onMouseEnter={() => setActiveDropdown(item.id)}
                  onClick={() => setActiveDropdown(activeDropdown === item.id ? null : item.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    isLightNav
                      ? (activeDropdown === item.id
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100')
                      : (activeDropdown === item.id 
                          ? 'bg-white/10 text-white' 
                          : 'text-white/80 hover:text-emerald-400 hover:bg-white/[0.06]')
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    activeDropdown === item.id ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Dropdown */}
                {activeDropdown === item.id && (
                  <div 
                    className={`absolute top-full mt-1.5 left-0 w-72 backdrop-blur-2xl rounded-xl shadow-2xl overflow-hidden z-50 border ${
                      isLightNav
                        ? 'bg-white border-slate-200 shadow-[0_20px_50px_rgba(15,23,42,0.12)]'
                        : 'bg-slate-900/95 border-white/10'
                    }`}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {item.items.map((subItem, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleNavClick(subItem.path)}
                        className={`w-full flex items-start gap-3 px-4 py-3 transition-all ${
                          subItem.highlight 
                            ? `bg-gradient-to-r ${item.gradient} text-white hover:brightness-110` 
                            : (isLightNav
                                ? 'hover:bg-slate-50 text-slate-800'
                                : 'hover:bg-white/[0.06] text-white')
                        } ${idx !== 0 ? (isLightNav ? 'border-t border-slate-100' : 'border-t border-white/[0.05]') : ''}`}
                      >
                        <subItem.icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          subItem.highlight ? 'text-white' : (isLightNav ? 'text-slate-400' : 'text-white/60')
                        }`} />
                        <div className="text-left">
                          <div className="font-medium text-sm">{subItem.label}</div>
                          <div className={`text-xs mt-0.5 ${
                            subItem.highlight ? 'text-white/80' : (isLightNav ? 'text-slate-500' : 'text-white/40')
                          }`}>
                            {subItem.desc}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <Button 
              onClick={() => handleNavClick('/location')} 
              size="sm"
              className={`ml-2 text-sm font-medium px-4 py-1.5 h-auto border ${
                isLightNav
                  ? 'bg-slate-100 hover:bg-slate-200 text-slate-900 border-slate-200'
                  : 'bg-[#132033] hover:bg-[#1b2b45] text-white border-white/[0.08]'
              }`}
            >
              <MapPin className="w-4 h-4 mr-1.5" />
              Location
            </Button>
            <Button 
              onClick={() => handleNavClick('/customer/login')} 
              size="sm"
              className={`ml-1.5 text-sm font-medium px-4 py-1.5 h-auto border ${
                isLightNav
                  ? 'bg-slate-100 hover:bg-slate-200 text-slate-900 border-slate-200'
                  : 'bg-[#132033] hover:bg-[#1b2b45] text-white border-white/[0.08]'
              }`}
              data-testid="header-login-btn"
            >
              <User className="w-4 h-4 mr-1.5" />
              Login
            </Button>
          </nav>

          {/* Mobile: Login button (compact) + Menu toggle */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={() => handleNavClick('/customer/login')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                isLightNav
                  ? 'bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-900 border-slate-200'
                  : 'bg-[#132033] hover:bg-[#1b2b45] active:bg-[#22345299] text-white border-white/[0.08]'
              }`}
              data-testid="header-mobile-login-btn"
              aria-label="Login to your account"
            >
              <User className="w-3.5 h-3.5" />
              <span>Login</span>
            </button>
            <button 
              className={`p-2 rounded-lg transition-colors ${
                isLightNav
                  ? 'hover:bg-slate-100 active:bg-slate-200'
                  : 'hover:bg-white/[0.08] active:bg-white/[0.12]'
              }`}
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${isLightNav ? 'text-slate-900' : 'text-white'}`} />
              ) : (
                <svg className={`w-6 h-6 ${isLightNav ? 'text-slate-900' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>

    {/* Mobile Menu - Full Screen Overlay */}
    {isMobileMenuOpen && (
      <div className="lg:hidden fixed inset-0 bg-slate-950/[0.98] backdrop-blur-xl z-[200]" data-mobile-menu data-testid="mobile-menu-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <button onClick={() => handleNavClick('/')} className="flex items-center">
            <picture>
              <source srcSet="/images/logo.webp" type="image/webp" />
              <img src="/images/logo.webp" alt="2A Electronics Service" className="h-8 object-contain" />
            </picture>
            <span 
              className="ml-2 font-bold text-sm tracking-tight text-white"
            >
              2A Electronics
            </span>
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-white"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="h-[calc(100%-56px)] overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
            {/* Navigation Cards */}
            {navItems.map((item) => (
              <div key={item.id} className="space-y-2">
                <div className={`flex items-center gap-2 px-2 py-1.5 text-sm font-extrabold uppercase tracking-wider ${item.id === 'repairs' ? 'text-cyan-300' : 'text-emerald-300'}`}>
                  <item.icon className={`w-4 h-4 ${item.id === 'repairs' ? 'text-cyan-300' : 'text-emerald-300'}`} />
                  {item.label}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {item.items.map((subItem, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNavClick(subItem.path); }}
                      className={`flex flex-col items-start p-3 rounded-xl transition-all active:scale-95 ${
                        subItem.highlight 
                          ? `bg-gradient-to-br ${item.gradient} text-white col-span-2` 
                          : 'bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/[0.05]'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <subItem.icon className="w-4 h-4" />
                        <span className="font-semibold text-sm text-white">{subItem.label}</span>
                      </div>
                      <span className={`text-xs ${subItem.highlight ? 'text-white/90' : 'text-white/70'}`}>
                        {subItem.desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Quick Links */}
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/[0.06]">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleNavClick('/blog'); }}
                className="flex items-center justify-center gap-2 p-3 bg-white/[0.04] rounded-xl text-white/80 hover:text-white transition-colors border border-white/[0.05]"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm">Blog</span>
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleNavClick('/customer/login'); }}
                className="flex items-center justify-center gap-2 p-3 bg-white/[0.04] rounded-xl text-white/80 hover:text-white transition-colors border border-white/[0.05]"
              >
                <User className="w-4 h-4" />
                <span className="text-sm">Account</span>
              </button>
            </div>

            {/* Core credentials, featured right above the booking / inquiry CTAs */}
            <CoreTrustBadges dark compact className="pt-3 border-t border-white/[0.06]" />

            {/* Location CTA */}
            <Button 
              onClick={() => handleNavClick('/location')} 
              className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white font-bold text-base rounded-xl"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Visit Our Store
            </Button>

            <div className="text-center text-white/50 text-xs pt-3">
              <p>4859 W Irving Park Rd, Chicago</p>
              <p>Mon-Fri 10am-6pm, Sat 10am-4pm | (773) 766-5814</p>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;
